import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme, css } from 'styled-components';
import throttle from 'lodash/throttle';
import isNull from 'lodash/isNull';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import { hook, withValue } from '@g4/automation-hooks';
import { useUserContext } from '@g4/next-furniture/UserContextProvider';
import dynamic from 'next/dynamic';
import tracking from '@g4/g4-analytics';

import {
  useAuthentication,
  useBookingPath,
  useSeatSizesMap,
  useSSRQuery,
} from '../../graphql';
import { useUiCart } from '../../graphql/localState/uiCart.hooks';
import { getPrice } from '@g4/next-furniture/Price/Price';
import { Layout, useMedia, Text } from '../Boneyard';
import Logo from './siteHeader/Logo';
import SkipToContent from './siteHeader/SkipToContent';
import Cart from './siteHeader/Cart';
import FlightSummary from './siteHeader/FlightSummary';
import BurgerMenu from './siteHeader/BurgerMenu';
import UserMenu from './siteHeader/UserMenu';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import useFlightSearchCriteria from '../../hooks/useFlightSearchCriteria';
import {
  useTranslations,
  withTranslations,
} from '../../graphql/components/TranslationsProvider';
import useBookingSteps from '../../hooks/useBookingSteps';

import SITE_HEADER_TRANSLATIONS from './siteHeader/siteHeaderTranslations.query.graphql.js';

import CART_WHITE_ICON from '../../images/ic-cart-white.svg';
import INDEX_PAGE_IMAGES_QUERY from './siteHeader/indexPageImages.query.graphql.js';

import {
  HEADER_LOGO,
  HEADER_CART_BUTTON,
  CART_POPOVER,
} from '../../../automationHooks.json';
import useStadiumGame from '../../hooks/useStadiumGame';
import { AgentBanner } from '@g4/next-furniture/AgentBanner';
import { useCart } from '../../graphql';
import usePaypal from '../../hooks/usePaypal.js';
import { getCartRawProducts } from '../../pageComponents/app/trackingItems';
import useIdleTimeout from '@g4/next-furniture/LoginSignup/hooks/useIdleTimeout.js';
import { useModal } from '../ModalProvider.js';

const AllegiantStadiumFlightSummary = dynamic(
  () => import('./siteHeader/AllegiantStadiumFlightSummary'),
  { ssr: false }
);

const propTypes = {
  theme: PropTypes.shape({
    layout: PropTypes.shape({
      headerBarHeight: PropTypes.number.isRequired,
      headerBarHeightMd: PropTypes.number.isRequired,
      headerBarHeightImpersonation: PropTypes.number.isRequired,
    }),
    zIndex: PropTypes.shape({
      siteHeader: PropTypes.number.isRequired,
    }),
  }),
  hideBookingSummary: PropTypes.bool,
  hideCart: PropTypes.bool,
  noShadow: PropTypes.bool,
  isStadium: PropTypes.bool,
  isUpliftInitialised: PropTypes.bool,
  setIsUpliftInitialised: PropTypes.func,
  pageContext: PropTypes.string,
};

const defaultProps = {
  hideBookingSummary: false,
  hideCart: false,
  noShadow: false,
  isStadium: false,
};

const TOP_BAR_HEIGHT = 44;
const HEADER_HEIGHT_SMALL = 50;
const HEADER_HEIGHT_LARGE = 86;

const Header = styled(Layout.Header)`
  position: relative;
  width: 100%;
  z-index: ${props => props.theme.zIndex.siteHeader};

  ${props => props.theme.query.lg} {
    position: fixed;
  }

  ${props =>
    props.noShadow
      ? css`
          background-color: ${props =>
            props.theme.colors.siteHeader.background.from};
        `
      : css`
          background-image: linear-gradient(
            ${props =>
              props.isStadium
                ? props.theme.colors.siteHeader.stadiumBackground.from
                : props.theme.colors.siteHeader.background.from},
            ${props =>
              props.isStadium
                ? props.theme.colors.siteHeader.stadiumBackground.to
                : props.theme.colors.siteHeader.background.to}
          );
          box-shadow: 0 1px 5px 0 #00000033;
        `}
  color: ${props => props.theme.colors.siteHeader.text};
`;

const Constrain = styled.div`
  position: relative;
  width: 100%;
  max-width: 1200px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-left: auto;
  margin-right: auto;
  padding: 0 calc(${props => props.theme.spacing(6)} / 2);

  ${props => props.theme.query.lg} {
    flex-wrap: nowrap;
  }
`;

const RightSideMenu = styled.div`
  display: flex;
  order: 2;
`;

const CartButton = styled.button.attrs({
  type: 'button',
  'aria-label': 'Cart',
})`
  display: flex;
  color: inherit;
  align-items: flex-end;
  padding: 10px 0;
  flex-shrink: 0;
  margin-left: ${props =>
    props.theme.spacing(props.isStadium && props.isMobile ? 1 : 2)};
  ${props => props.theme.query.md} {
    padding: 16px 0;
  }
`;

const TripTotalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const NoShrinkText = styled(Text)`
  flex-shrink: 0;
  white-space: nowrap;
`;

/*
  If we fail to render CartVisibilityWrapper, we lose references to any popover
  content within it, which creates problems for useOnClickOutside. Instead
  set the visibility to none.
 */
const CartVisibilityWrapper = styled.div`
  ${props =>
    !props.visible &&
    `
    display: none;
  `}
`;

const CartIcon = styled.img.attrs({
  src: CART_WHITE_ICON,
  alt: '',
})`
  width: 20px;
  height: 20px;
  margin-left: ${props => props.theme.spacing(2)};
  margin-bottom: 5px;
`;

const SiteHeader = ({
  theme,
  pageContext,
  hideCart,
  hideBookingSummary,
  noShadow,
  isStadium,
  isUpliftInitialised,
  setIsUpliftInitialised,
}) => {
  const [{ isOpen: isCartOpen }, updateUiCart] = useUiCart();
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [hasScrolled, setHasScrolled] = React.useState(false);
  const [isLoyaltyPaymentUsed, setIsLoyaltyPaymentUsed] = React.useState(false);
  const { steps, isChangingRoute } = useBookingSteps();
  const { operatedBy } = useBookingPath();
  const { isImpersonation, isAdmin, agentType } = useUserContext();
  const { isAnonymousUser } = useAuthentication();
  const displayLoginButton = isNull(agentType) || !isAnonymousUser;
  const flightSearchCriteria = useFlightSearchCriteria();

  const translations = useTranslations();
  const {
    setShowModal,
    setContent,
    setTitle,
    isModalAutoClosed,
    setIsModalAutoClosed,
    setIsMobile,
  } = useModal();

  useIdleTimeout(
    setShowModal,
    setContent,
    setTitle,
    translations,
    isModalAutoClosed,
    setIsModalAutoClosed,
    setIsMobile
  );

  const getStep = key => steps.find(x => x.key === key);
  const isPaymentStep = getStep('payment').isActive;
  const setIsCartOpen = useCallback(
    isOpen => {
      updateUiCart({ isOpen });
    },
    [updateUiCart]
  );

  const maxHeaderHeightLg = HEADER_HEIGHT_LARGE + TOP_BAR_HEIGHT;

  const [stadiumGame] = useStadiumGame();

  const { seatSizesMap } = useSeatSizesMap();

  const getCartItems = cartItems => {
    return {
      currency: 'USD',
      value: cart?.order?.price?.total,
      products: cartItems,
    };
  };

  useEffect(() => {
    const onScroll = throttle(() => {
      setHasScrolled(
        document.body.scrollTop > maxHeaderHeightLg ||
          document.documentElement.scrollTop > maxHeaderHeightLg
      );
    });

    window.addEventListener('scroll', onScroll);
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [maxHeaderHeightLg]);

  useEffect(() => {
    if (isChangingRoute) {
      setIsCartOpen(false);
    }
  }, [isChangingRoute, setIsCartOpen]);

  const closeHeaderExpandables = () => {
    if (isPaymentStep) {
      return;
    }
    setIsCartOpen(false);
    setIsFormOpen(false);
  };

  const toggleIsFormOpen = () => {
    if (!isFormOpen) {
      closeHeaderExpandables();
    }

    setIsFormOpen(prevState => !prevState);
  };

  const toggleIsCartOpen = () => {
    if (!isCartOpen) {
      closeHeaderExpandables();
    }
    const cartItems = getCartRawProducts(
      cart,
      operatedBy,
      flightSearchCriteria.data,
      seatSizesMap
    );
    if (!cartItems.length) {
      tracking.trackCartClick(getCartItems(cartItems));
    } else {
      //Trigger event as multiple hits when payload exceeds maximum limit
      cartItems.forEach((rawProduct, index) => {
        //Set timeout so that all dataLayer pushes triggers corresponding ga4 event - to ensure no triggers are missed
        setTimeout(() => {
          tracking.trackCartClick(getCartItems(rawProduct));
        }, index * 1000);
      });
    }
    setIsCartOpen(!isCartOpen);
  };

  const { data: headerBannerData } = useSSRQuery(INDEX_PAGE_IMAGES_QUERY);

  const allegiantStadiumLogo = headerBannerData?.application?.images?.find(
    image => image.id === 'stadiumLogoCart'
  );

  const allegiantStadiumPackageLogo = headerBannerData?.application?.images?.find(
    image => image.id === 'allegiantStadiumLogo'
  );

  const siteHeaderRef = React.useRef();
  const mq = useMedia();
  const isMobile = mq.lte('sm');

  const headerHeightMobile = HEADER_HEIGHT_SMALL;
  const headerHeightDesktop = hasScrolled
    ? HEADER_HEIGHT_SMALL
    : HEADER_HEIGHT_LARGE;
  const headerHeight = mq.lt('md') ? headerHeightMobile : headerHeightDesktop;

  // Do not close the menu if we are interacting with popover content
  // This checks for all of our known popover content and includes in our
  // useOnClickOutside hook, which might otherwise not work as intended
  // We could reference specific popover contents that appear within the cart,
  // but better for this approach to be generic and eventually refactored.
  // Popover content remaining after cart has closed is an intermittent,
  // hard-to-replicate bug.
  let portalEls = [];
  if (typeof document === 'object') {
    const g4PopoverHtmlCollection = document.getElementsByClassName(
      'g4-popover-content'
    );
    portalEls = [].slice.call(g4PopoverHtmlCollection).map(el => ({
      current: el,
    }));
  }

  // attach any found portalEls
  useOnClickOutside([siteHeaderRef, ...portalEls], closeHeaderExpandables);
  const onRequestClose = useCallback(() => setIsCartOpen(false), [
    setIsCartOpen,
  ]);

  const { cart, cartError } = useCart();

  if (cartError) {
    throw cartError;
  }

  const orderItems = groupBy(cart?.order?.items, '__typename');

  /**
   * isMinimumElementsForStadiumPackage is bool response on required minimum elements for an Allegiant Stadium Package" consist on the following
   * 1. Game tickets
   * 2. Flights
   * 3. Minimum 2 night hotel stay
   * @returns {Boolean}
   */
  const [
    isMinimumElementsForStadiumPackage,
    setIsMinimumElementsForStadiumPackage,
  ] = useState(false);

  useEffect(() => {
    setIsMinimumElementsForStadiumPackage(
      !orderItems.FlightOrderItem ||
        !orderItems.ShowOrderItem?.[0] ||
        !orderItems.HotelOrderItem?.[0]
    );
  }, [orderItems]);

  usePaypal(
    'messages',
    isStadium && isMinimumElementsForStadiumPackage
      ? 0
      : cart?.order?.price?.balanceDue
  );

  useEffect(() => {
    if (cart) {
      const payments = cart?.order?.payments;
      if (payments) {
        payments.forEach(payment => {
          if (payment.__typename === 'LoyaltyPayment') {
            setIsLoyaltyPaymentUsed(true);
          }
        });
      }
    }
  }, [cart, setIsLoyaltyPaymentUsed]);

  return (
    <div
      ref={siteHeaderRef}
      style={{
        // Set the height of the header container when `position: fixed` to push the page content down
        height: mq.lte('md')
          ? 'auto'
          : isImpersonation || isAdmin
          ? theme.layout.headerBarHeightImpersonation
          : theme.layout.headerBarHeightMd,
      }}
    >
      <SkipToContent>Skip to content</SkipToContent>

      <Header as="header" noShadow={noShadow} isStadium={isStadium}>
        <AgentBanner />
        <Constrain>
          <Logo
            logoHeight={
              mq.lt('md')
                ? theme.layout.headerBarHeight
                : theme.layout.headerBarHeightMd
            }
            automationHook={HEADER_LOGO.hook}
            isStadium={isStadium}
            allegiantStadiumPackageLogo={allegiantStadiumPackageLogo}
          />
          {!flightSearchCriteria.loading &&
            flightSearchCriteria.data &&
            !hideBookingSummary &&
            (!isStadium ? (
              <FlightSummary
                flightSearchCriteria={flightSearchCriteria.data}
                isFormOpen={isFormOpen}
                onFormToggle={toggleIsFormOpen}
              />
            ) : (
              <AllegiantStadiumFlightSummary
                flightSearchCriteria={flightSearchCriteria.data}
                gameDetails={stadiumGame}
                orderItems={cart?.order}
              />
            ))}
          <RightSideMenu>
            {mq.gte('md') && displayLoginButton && (
              <UserMenu
                pageContext={pageContext}
                isLoyaltyPaymentUsed={isLoyaltyPaymentUsed}
              />
            )}
            {!hideCart && (
              <CartButton
                onClick={toggleIsCartOpen}
                {...hook(HEADER_CART_BUTTON.hook)}
                isStadium={isStadium}
                isMobile={isMobile}
              >
                <TripTotalContainer
                  aria-label={`Trip Total ${
                    getPrice(get(cart, 'order.price.total')).price
                  }`}
                >
                  <NoShrinkText
                    size={12}
                    lh={mq.gte('md') ? 1.67 : 1}
                    automationHook={withValue(HEADER_CART_BUTTON.hook, 'label')}
                  >
                    Trip Total:
                  </NoShrinkText>
                  <Text
                    size={14}
                    weight={600}
                    automationHook={withValue(HEADER_CART_BUTTON.hook, 'price')}
                  >
                    {isStadium && isMinimumElementsForStadiumPackage
                      ? '$ --.--'
                      : getPrice(get(cart, 'order.price.total')).price}
                  </Text>
                </TripTotalContainer>
                <CartIcon {...hook(HEADER_CART_BUTTON.hook, 'icon')} />
              </CartButton>
            )}

            {mq.lt('md') && (
              <BurgerMenu
                headerHeight={headerHeight}
                topBarOffset={mq.gte('md') ? TOP_BAR_HEIGHT : 0}
              >
                <UserMenu
                  pageContext={pageContext}
                  isLoyaltyPaymentUsed={isLoyaltyPaymentUsed}
                  showInsideDrawer={mq.lt('md')}
                  isBurgerMenu
                />
              </BurgerMenu>
            )}
          </RightSideMenu>
          {!hideCart && (
            <CartVisibilityWrapper
              visible={isPaymentStep ? false : isCartOpen}
              {...hook(CART_POPOVER.hook, 'wrapper')}
            >
              <Cart
                data={cart}
                onRequestClose={onRequestClose}
                automationHook={CART_POPOVER.hook}
                isMinimumElementsForStadiumPackage={
                  isMinimumElementsForStadiumPackage
                }
                allegiantStadiumLogo={allegiantStadiumLogo}
                isUpliftInitialised={isUpliftInitialised}
                setIsUpliftInitialised={setIsUpliftInitialised}
                isPaymentStep={isPaymentStep}
              />
            </CartVisibilityWrapper>
          )}
        </Constrain>
      </Header>
    </div>
  );
};

SiteHeader.propTypes = propTypes;
SiteHeader.defaultProps = defaultProps;

export default withTheme(
  withTranslations(SITE_HEADER_TRANSLATIONS, SiteHeader)
);
